/**
 * This is a component that renders numbered pagination buttons.
 *
 * Here are some examples with props.
 * Note that indexes are 0 based.
 *
 * currentPageIndex: 3
 * lastPageIndex: 5
 * < 1 2 3 [4] 5 6 >
 *
 * currentPageIndex: 0
 * lastPageIndex: 49
 * [1] 2 3 4 5 ... 50 >
 *
 * currentPageIndex: 2
 * lastPageIndex: 49
 * < 1 2 [3] 4 5 ... 50 >
 *
 * currentPageIndex: 45
 * lastPageIndex: 49
 * < 1 ... [46] 47 48 49 50 >
 *
 * currentPageIndex: 49
 * lastPageIndex: 49
 * < 1 ... 46 47 48 49 [50]
 */
import {t} from "@lingui/core/macro";
import {StyleSheet} from "aphrodite";
import * as React from "react";

import Button from "@khanacademy/wonder-blocks-button";
import {View} from "@khanacademy/wonder-blocks-core";
import IconButton from "@khanacademy/wonder-blocks-icon-button";
import {Strut} from "@khanacademy/wonder-blocks-layout";
import {color, spacing} from "@khanacademy/wonder-blocks-tokens";
import {LabelSmall} from "@khanacademy/wonder-blocks-typography";

import {getMiddlePageIndexes} from "./numbered-pagination-util.ts";

import caretLeftIcon from "@phosphor-icons/core/regular/caret-left.svg";
import caretRightIcon from "@phosphor-icons/core/regular/caret-right.svg";

const FIRST_PAGE_INDEX = 0;

type Props = {
    currentPageIndex: number;
    lastPageIndex: number;
    fetchPage: (pageIndex: number) => void;
};

class NumberedPaginationButtons extends React.Component<Props> {
    renderNumberedPaginationButton: (pageIndex: number) => React.ReactNode = (
        pageIndex: number,
    ) => {
        const {currentPageIndex, fetchPage} = this.props;

        if (currentPageIndex === pageIndex) {
            return (
                <LabelSmall style={styles.currentPage} key={pageIndex}>
                    {(pageIndex + 1).toString()}
                </LabelSmall>
            );
        }

        return (
            <Button
                style={styles.numberBtn}
                key={pageIndex}
                kind="tertiary"
                onClick={() => fetchPage(pageIndex)}
                size="small"
            >
                {(pageIndex + 1).toString()}
            </Button>
        );
    };

    renderEllipsis(): React.ReactNode {
        return <LabelSmall style={styles.ellipsis}>...</LabelSmall>;
    }

    render(): React.ReactNode {
        const {currentPageIndex, lastPageIndex, fetchPage} = this.props;

        if (
            // lastPageIndex is same as first index, that means that there's
            // only 1 page.
            lastPageIndex === FIRST_PAGE_INDEX ||
            // When the current page index is out of available page range,
            // something must have gone wrong so hide the buttons.
            currentPageIndex < FIRST_PAGE_INDEX ||
            currentPageIndex > lastPageIndex
        ) {
            return null;
        }

        // We always show 1 and the last page so we need to get the page
        // indexes that are in between those.
        const middlePageIndexes = getMiddlePageIndexes(
            currentPageIndex,
            lastPageIndex,
        );
        const midLength = middlePageIndexes.length;

        return (
            <View style={styles.numberedPaginationContainer}>
                {/* "<" button. Render when prev page exists */}
                {currentPageIndex !== FIRST_PAGE_INDEX && (
                    <IconButton
                        aria-label={t`previous`}
                        icon={caretLeftIcon}
                        kind="tertiary"
                        onClick={() => fetchPage(currentPageIndex - 1)}
                    />
                )}
                <Strut size={spacing.xSmall_8} />
                {/* The first page should always be rendered. */}
                {this.renderNumberedPaginationButton(FIRST_PAGE_INDEX)}
                {/*
                    For pages like "1 2 3 4 5 6", we don't show ellipsis.
                    For pages like "1 ... 7 8 9", we should show ellipsis.
                    Therefore, when there are multiple pages in between the
                    first and the last page and the next number after 1 is
                    not 2, we should show the ellipsis here.
                */}
                {midLength > 0 &&
                    middlePageIndexes[0] !== FIRST_PAGE_INDEX + 1 &&
                    this.renderEllipsis()}
                {/* Render the pages in between the first and last page. */}
                {middlePageIndexes.map(this.renderNumberedPaginationButton)}
                {/*
                    For pages like "6 7 8 9 10", we don't show ellipsis.
                    For pages like "6 7 8 ... 15", we should show ellipsis.
                    Therefore, when there are multiple pages in between the
                    first and the last page and the next number before the
                    last page is not last page index - 1, we should show the
                    ellipsis here.
                */}
                {midLength > 0 &&
                    middlePageIndexes[midLength - 1] !== lastPageIndex - 1 &&
                    this.renderEllipsis()}
                {/*
                    The last page should be rendered if there are more than
                    one page.
                */}
                {lastPageIndex !== 0 &&
                    this.renderNumberedPaginationButton(lastPageIndex)}
                <Strut size={spacing.xSmall_8} />
                {/* ">" button. Render when next page exists */}
                {currentPageIndex !== lastPageIndex && (
                    <IconButton
                        aria-label={t`next`}
                        icon={caretRightIcon}
                        kind="tertiary"
                        onClick={() => fetchPage(currentPageIndex + 1)}
                    />
                )}
            </View>
        );
    }
}

const styles = StyleSheet.create({
    numberedPaginationContainer: {
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "center",
    },
    currentPage: {
        fontWeight: "bold",
        border: `1px solid ${color.offBlack16}`,
        borderRadius: 4,
        minWidth: 32,
        minHeight: 32,
        boxSizing: "border-box",
        display: "inline-flex",
        justifyContent: "center",
        alignItems: "center",
        padding: `${spacing.xxSmall_6}px ${spacing.xSmall_8}px`,
    },
    numberBtn: {
        marginLeft: spacing.small_12,
        marginRight: spacing.small_12,
    },
    ellipsis: {
        fontWeight: "bold",
        color: color.offBlack32,
    },
});

export default NumberedPaginationButtons;
